import { useState, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@/lib/types";
import { Link, useLocation } from "wouter";
import { Loader2, CheckCircle2, AlertCircle, ExternalLink, Info } from "lucide-react";
import { useForm } from "react-hook-form";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ManagerLayout from "@/components/layout/ManagerLayout";
import AgentLayout from "@/components/layout/AgentLayout";
import { ManualBankDetailsForm } from "@/components/payments/ManualBankDetailsForm";

interface StripeAccountStatus {
  connected: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  requirements?: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
    pending_verification?: string[];
    disabled_reason?: string;
  };
  message?: string;
}

type BankDetailsForm = {
  bankName: string;
  ibanNumber: string;
  swiftCode?: string;
  country: string;
  bankCode: string;
};

function getReadableVerificationLabel(item: string): string {
  if (item.includes('passport')) return 'Passport';
  if (item.includes('company_authorization')) return 'Company Authorization Document';
  if (item.includes('verification.document')) return 'Verification Document';
  if (item.includes('documents')) return 'Identity Document';
  // Add more mappings as needed
  return item.replace(/_/g, ' ');
}

export default function StripeConnectPage() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [location, navigate] = useLocation();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  // Get Stripe account status
  const { data: accountStatus, isLoading: isStatusLoading, refetch } = useQuery<StripeAccountStatus>({
    queryKey: ["stripe/account-status"],
    queryFn: () => apiRequest("GET", "stripe/account-status"),
    enabled: !!user,
  });

  // Create Stripe Connect account
  const { mutate: createAccount, isPending: isCreating } = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "stripe/account", {});
      // return await res.json();
      return res;
    },
    onSuccess: (data) => {
      setRedirectUrl(data.url);
      // Redirect to Stripe
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create Stripe account. Please try again.",
        variant: "destructive",
      });
    },
  });

  const isLoadingCombined = isLoading ||isStatusLoading || isCreating;
  
  useEffect(() => {
    // Refresh account status when user returns from Stripe
    if (location.includes('stripe-connect')) {
      refetch();
    }
  }, [location, refetch]);

  // Add this after your hooks
  const { register, handleSubmit, formState: { errors }, reset } = useForm<BankDetailsForm>();
  const [bankUpdateLoading, setBankUpdateLoading] = useState(false);

  // Add local state for refresh loading
  const [refreshing, setRefreshing] = useState(false);

  const onSubmitBankDetails = async (data: BankDetailsForm) => {
    setBankUpdateLoading(true);
    try {
      const res = await apiRequest("PATCH", "user/bank-details", data);
      if (res.success) {
        toast({ title: "Success", description: "Bank details updated." });
        reset();
        refetch(); // Optionally refresh account status
      } else {
        const err = res.error;
        toast({ title: "Error", description: err.message || "Failed to update bank details.", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to update bank details.", variant: "destructive" });
    } finally {
      setBankUpdateLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-16 flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="container py-16 flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold mb-4">Please log in to connect your bank account</h1>
          <Button asChild>
            <Link href="/auth">Log In</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  // Only allow manager and agent roles
  if (user.role !== UserRole.MANAGER && user.role !== UserRole.AGENT) {
    return (
      <Layout>
        <div className="container py-16">
          <Card>
            <CardHeader>
              <CardTitle>Stripe Connect</CardTitle>
              <CardDescription>Connect your Stripe account to receive payments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 p-4 bg-amber-50 text-amber-900 rounded-md border border-amber-200">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p>Only property managers and agents can connect their bank accounts to receive payments.</p>
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" asChild>
                <Link href="/dashboard">Back to Dashboard</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </Layout>
    );
  }

  // Choose layout based on user role
  let RoleLayout: React.ComponentType<{ children: React.ReactNode }> = Layout;
  if (user.role === UserRole.MANAGER) RoleLayout = ManagerLayout;
  else if (user.role === UserRole.AGENT) RoleLayout = AgentLayout;

  const showActionButton =
    !!accountStatus &&
    accountStatus.requirements?.disabled_reason !== 'requirements.pending_verification' &&
    (!accountStatus.detailsSubmitted || (!accountStatus.payoutsEnabled && accountStatus.detailsSubmitted));

  return (
    <RoleLayout>
      <div className="container py-12">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle className="flex items-center gap-2">
                  Stripe Connect
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" tabIndex={0} title="Why connect your Stripe account?" className="ml-1 p-0 bg-transparent border-0 cursor-pointer text-muted-foreground align-middle">
                          <Info className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs text-sm">
                        <div className="font-medium mb-1">Why connect your Stripe account?</div>
                        <ul className="list-disc pl-4 space-y-1">
                          <li>Receive payments directly to your bank account automatically when guests book your properties.</li>
                          <li>Our system automatically handles payment splits between property managers and agents.</li>
                          <li>Receive payments in AED (UAE Dirham) directly to your local bank account.</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
              </div>
              <CardDescription>Connect your bank account to receive payments</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingCombined ? (
                <div className="py-12 flex flex-col items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                  <p>Loading your account information...</p>
                </div>
              ) : (
                <>
                  {accountStatus ? (
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Account Status Section */}
                      <div className="flex-1 rounded-lg border p-4">
                        <h3 className="text-lg font-medium mb-4">Account Status</h3>
                        {accountStatus.connected ? (
                          <div className="space-y-4">
                            {/* Account Setup Status */}
                            <div className="flex flex-col">
                              <div className="flex items-center gap-3">
                                <div className={`rounded-full p-1 ${accountStatus.detailsSubmitted ? 'bg-green-100' : 'bg-amber-100'}`}>
                                  {accountStatus.detailsSubmitted ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 text-amber-600" />
                                  )}
                                </div>
                                <span className={`font-medium ${accountStatus.detailsSubmitted ? 'text-green-700' : 'text-yellow-700'}`}>Account Setup: {accountStatus.detailsSubmitted ? 'Complete' : 'Pending'}</span>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">
                                  {accountStatus.detailsSubmitted
                                    ? "You have completed your account setup."
                                    : "Your account setup is incomplete."}
                                </p>
                              </div>
                            </div>
                            {/* Payouts Status */}
                            <div className="flex flex-col">
                              <div className="flex items-center gap-3">
                                <div className={`rounded-full p-1 ${accountStatus.payoutsEnabled ? 'bg-green-100' : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'bg-yellow-100' : 'bg-red-100'}`}>
                                  {accountStatus.payoutsEnabled ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  ) : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? (
                                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                  )}
                                </div>
                                <span className={`font-medium ${accountStatus.payoutsEnabled ? 'text-green-700' : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'text-yellow-700' : 'text-red-700'}`}>Payouts: {accountStatus.payoutsEnabled ? 'Complete' : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'Pending' : 'Paused'}</span>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">
                                  {accountStatus.payoutsEnabled
                                    ? "You can receive payouts to your bank account."
                                    : "Payouts to your bank account are not enabled yet. You must complete your Stripe onboarding and provide all required information to enable payouts."}
                                </p>
                              </div>
                            </div>
                            {/* Payments Status */}
                            <div className="flex flex-col">
                              <div className="flex items-center gap-3">
                                <div className={`rounded-full p-1 ${accountStatus.chargesEnabled ? 'bg-green-100' : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'bg-yellow-100' : 'bg-red-100'}`}>
                                  {accountStatus.chargesEnabled ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  ) : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? (
                                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                  )}
                                </div>
                                <span className={`font-medium ${accountStatus.chargesEnabled ? 'text-green-700' : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'text-yellow-700' : 'text-red-700'}`}>Payments: {accountStatus.chargesEnabled ? 'Complete' : accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'Pending' : 'Paused'}</span>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">
                                  {accountStatus.chargesEnabled
                                    ? "You can receive payments from guests."
                                    : "You cannot receive payments yet. Complete your Stripe setup to enable payments."}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-yellow-600 font-semibold mb-2">
                            Please connect your Stripe account to receive payments.
                          </div>
                        )}
                        {accountStatus && showActionButton && (
                          !accountStatus.detailsSubmitted ? (
                            <Button
                              onClick={() => createAccount()}
                              disabled={isLoadingCombined}
                              className="mt-6 w-full md:w-auto"
                            >
                              Complete Account Setup
                            </Button>
                          ) : (
                            <Button
                              onClick={() => createAccount()}
                              disabled={isLoadingCombined}
                              className="mt-6 w-full md:w-auto"
                            >
                              Connect to Stripe to Enable Payouts
                            </Button>
                          )
                        )}
                        {accountStatus && (
                          <Button
                            type="button"
                            onClick={async () => {
                              setRefreshing(true);
                              try {
                                await refetch();
                              } finally {
                                toast({ title: "Success", description: "Stripe account status refreshed." });
                                setRefreshing(false);
                              }
                            }}
                            className="mt-6 w-full md:w-auto"
                            disabled={isLoadingCombined || refreshing}
                          >
                             {refreshing ? <> <Loader2 className="h-4 w-4 animate-spin" /> Loading...</>: 'Refresh Status'}
                          </Button>
                        )}
                      </div>
                      {/* Required Actions Section */}
                      {accountStatus.requirements &&
                        ((accountStatus.requirements.past_due?.length ?? 0) > 0 ||
                          (accountStatus.requirements.currently_due?.length ?? 0) > 0 ||
                          (accountStatus.requirements.eventually_due?.length ?? 0) > 0 ||
                          (accountStatus.requirements.pending_verification?.length ?? 0) > 0) && (
                          <div className="flex-1 rounded-lg border p-4 bg-gray-50">
                            <h4 className={`font-medium mb-2 ${accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' ? 'text-yellow-700' : 'text-red-700'}`}> 
                              {accountStatus.requirements?.disabled_reason === 'requirements.pending_verification'
                                ? (
                                  <span className="flex items-center gap-2">
                                    Pending Verification
                                   
                                  </span>
                                )
                                : 'Required Actions'}
                            </h4>
                            {accountStatus.requirements.past_due?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-red-700">Past Due:</p>
                                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                                  {accountStatus.requirements.past_due.map((item, index) => (
                                    <li key={`pastdue-${index}`}>{item.replace(/_/g, ' ')}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {accountStatus.requirements.currently_due?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-amber-700">Due now:</p>
                                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                                  {accountStatus.requirements.currently_due.map((item, index) => (
                                    <li key={`current-${index}`}>{item.replace(/_/g, ' ')}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {accountStatus.requirements.eventually_due?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-blue-700">Eventually needed:</p>
                                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                                  {accountStatus.requirements.eventually_due.map((item, index) => (
                                    <li key={`eventual-${index}`}>{item.replace(/_/g, ' ')}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {accountStatus.requirements?.pending_verification?.length > 0 &&
                              accountStatus.requirements?.disabled_reason !== 'requirements.pending_verification' && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-purple-700">Pending Verification:</p>
                                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                                  {accountStatus.requirements?.pending_verification?.map((item, index) => (
                                    <li key={`pending-${index}`}>{getReadableVerificationLabel(item)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {accountStatus.requirements?.pending_verification?.length > 0 &&
                              accountStatus.requirements?.disabled_reason === 'requirements.pending_verification' && (
                              <ul className="list-disc pl-5 text-sm text-muted-foreground">
                                {accountStatus.requirements?.pending_verification?.map((item, index) => (
                                  <li key={`pending-main-${index}`}>{getReadableVerificationLabel(item)}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                    </div>
                  ) : null}
                  {!isLoadingCombined && !accountStatus?.connected && (
                    <ManualBankDetailsForm refetch={refetch} />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </RoleLayout>
  );
}